import { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import ApplicationBar, {
  toolbarHeight,
} from "./../../Components/ApplicationBar";

import NavigationDrawer from "./../../Components/NavigationDrawer";
import ProcessBar from "../../Components/ProcessBar";
import Footer from "../../Components/Footer";
import { Button } from "@mui/material";

import { getPrev, getNext, hasPrev, hasNext } from "../../siteMap/index";
import { useLocation } from "react-router";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import service from "../../helpers/GifService";
import { resetForm, resetServiceFunction } from "../../../../store/formSlice";
import { canEditSelector } from "../../../../store/applicantsSlice";
import { proposalSelector } from "../../../../store/proposalSlice";
import { contactSelector } from "../../../../store/userSlice";
import { useNotify } from "../../../../providers/NotificationProvider";
import { PRE_PROPOSAL_STATUS } from "../../Constants";


const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  content: {
    flexGrow: 1,
    overflow: "auto", 
    marginTop: toolbarHeight,
    marginBottom: toolbarHeight,
    height: `calc(100vh - ${toolbarHeight * 2}px)`,
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(2),
  },
  mainPaper: {
    padding: theme.spacing(2),
    display: "flex",
    height: `calc(100vh - ${toolbarHeight * 2 + 190}px)`,
    overflow: "auto",
    flexDirection: "column",
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  actionsWrapper: {
    display: "flex",
    justifyContent: "space-between",
  },
  actionButton: {
    "&:hover": {
      backgroundColor: theme.palette.gif.blue.hover,
    },
    color: theme.palette.gif.blue.color,
    backgroundColor: theme.palette.gif.blue.background,
  },
}));

export default function MainLayout({ children, navigation, baseurl }) {
  const classes = useStyles();
  const localtion = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();
  const notify = useNotify();
  const proposal = useSelector(proposalSelector);
  const contact = useSelector(contactSelector);
  const canEdit = useSelector(canEditSelector);
  const currentForm = useSelector((state) => state.currentForm);
  const [menuOpen, setMenuOpen] = useState(true);
  const [needSave, setNeedSave] = useState(false);
  const [needPrev, setNeedPrev] = useState(true);
  const [needNext, setNeedNext] = useState(true);

  const setOpen = () => {
    setMenuOpen(!menuOpen);
  };

  const submitCurrentForm = () => {
    const formData = currentForm.data.payload;
    const func = currentForm.serviceFunction.payload;
    if (func == null) return;
    if (formData == null) return;

    if (!service[func])
      // this is set by setServiceFunction inside a form page.
      throw new Error(`gif services dosent have a function called ${func}`);

    service[func](formData);
    dispatch(resetForm());
  };

  const handleBack = () => {
    const relativePath = localtion.pathname.replace(baseurl, "");
    const route = getPrev(
      relativePath,
      proposal.programType,
      proposal.status === PRE_PROPOSAL_STATUS
    );
    submitCurrentForm();
    dispatch(resetServiceFunction());
    if (route) {
      history.push(`${baseurl}${route}`);
    }
  };
  const handleNext = () => {
    const relativePath = localtion.pathname.replace(baseurl, "");
    const route = getNext(
      relativePath,
      proposal.programType,
      proposal.status === PRE_PROPOSAL_STATUS
    );
    submitCurrentForm();
    dispatch(resetServiceFunction());
    if (route) {
      history.push(`${baseurl}${route}`);
    }
  };
  const hasPrevPage = useCallback((relativePath, programType, status) => {
    return hasPrev(
      relativePath,
      programType,
      status === PRE_PROPOSAL_STATUS
    );  
  },[]);
  const hasNextPage = useCallback((relativePath, programType, status) => {
    return hasNext(
      relativePath,
      programType,
      status === PRE_PROPOSAL_STATUS
    );
  },[]);

  useEffect(() => {
    if(!proposal)return;
    const relativePath = localtion.pathname.replace(baseurl, "");
    setNeedPrev(()=>hasPrevPage(relativePath, proposal.programType, proposal.status));
    setNeedNext(()=>hasNextPage(relativePath, proposal.programType, proposal.status));
  }, [baseurl,hasPrevPage, hasNextPage, localtion.pathname, proposal]);

  useEffect(() => {
    const func = currentForm.serviceFunction.payload;
    if (service[func] !== undefined) setNeedSave(true);
    else setNeedSave(false);
  }, [currentForm.serviceFunction.payload]);

  useEffect(() => {
    return () => {
      dispatch(resetForm());
    };
  }, [dispatch]);

  return (
    <div className={classes.root}>
      <CssBaseline />
      <ApplicationBar setOpen={setOpen} />
      <NavigationDrawer
        userName={contact?.displayname}
        open={menuOpen}
        navigation={navigation}
      />
      <main className={classes.content}>
        <Container maxWidth="lg" className={classes.container}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <ProcessBar baseurl={baseurl} />
            </Grid>
            <Grid item xs={12}>
              <Paper className={classes.mainPaper}>{children}</Paper>
            </Grid>
          </Grid>
          <Box pt={2} className={classes.actionsWrapper}>
            <Button
              variant="contained"
              className={classes.actionButton}
              disableElevation
              onClick={handleBack}
              disabled={!needPrev}
            >
              Back
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                className={classes.actionButton}
                disableElevation
                onClick={() => {
                  submitCurrentForm();
                  notify.show("Saving..");
                }}
                disabled={!needSave}
              >
                Save
              </Button>
            )}
            <Button
              variant="contained"
              className={classes.actionButton}
              disableElevation
              onClick={handleNext}
              disabled={!needNext}
            >
              Next
            </Button>
          </Box>
        </Container>
      </main>
      <Footer />
    </div>
  );
}
